import { useState, useCallback } from 'react';
import { turnoApi } from '../api/turnoApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';
import { fechaTurnoToSortKey, dateTimeToSortKey } from '../../utils/dateUtils';

/**
 * Hook personalizado para gestión de turnos
 * Proporciona estado y funciones para operaciones CRUD de turnos
 */
export const useTurnos = () => {
  const [turnos, setTurnos] = useState([]);
  const [turnosTotal, setTurnosTotal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene turnos con filtros opcionales
   */
  const fetchTurnos = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.getAll(filters);
      setTurnos(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar turnos');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene turnos totales con filtros
   */
  const fetchTurnosTotal = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.getTurnosTotal(filters);
      setTurnosTotal(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar turnos totales');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene turnos totales por proyecto específico
   */
  const fetchTurnosByProyecto = useCallback(async (idProyecto) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.getTurnosTotal({ idProyecto });
      
      // Ordenar: 1) fecha del turno (YYYYMMDD), 2) created_at (sin new Date en fecha_turno)
      const sortedTurnos = (response.data || []).sort((a, b) => {
        const fechaA = fechaTurnoToSortKey(a.fecha_turno);
        const fechaB = fechaTurnoToSortKey(b.fecha_turno);
        if (fechaB !== fechaA) {
          return fechaB - fechaA;
        }
        return dateTimeToSortKey(b.created_at) - dateTimeToSortKey(a.created_at);
      });
      
      setTurnos(sortedTurnos); // Usar setTurnos para que se muestre en la tabla
      return sortedTurnos;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar turnos del proyecto');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo turno detalle
   */
  const createTurnoDetalle = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.create(data);
      // showSuccessToast('Turno detalle creado exitosamente'); // Comentado por solicitud del usuario
      
      // OPTIMIZADO: No refrescar automáticamente (el modal maneja su propio refresh)
      // await fetchTurnos(); // ← ELIMINADO: Causaba 15+ segundos de lentitud
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el turno detalle');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // Sin dependencias - no necesita fetchTurnos


  /**
   * Actualiza un turno existente
   */
  const updateTurno = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.update(id, data);
      showSuccessToast('Turno actualizado exitosamente');
      
      // Refrescar la lista
      await fetchTurnos();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el turno');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTurnos]);

  /**
   * Crea un turno cabecera
   */
  const createTurnoCabecera = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.createTurnoTotal(data);
      // Mensaje de éxito manejado por el componente que usa este hook
      
      // Refrescar la lista
      await fetchTurnosTotal();
      
      return response.data;
    } catch (err) {
      // No mostrar toast automático para errores de turno duplicado, 
      // dejarlo al componente que maneja el caso específico
      if (err.response && err.response.status === 422 && err.response.data?.error === 'TURNO_DUPLICADO') {
        setError('Turno duplicado');
      } else {
        const errorMessage = showErrorToast(err, 'Error al crear el turno total');
        setError(errorMessage);
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTurnosTotal]);

  /**
   * Actualiza un turno cabecera
   */
  const updateTurnoCabecera = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.updateTurnoCabecera(id, data);
      // Mensaje de éxito manejado por el componente que usa este hook
      
      // Refrescar la lista
      await fetchTurnosTotal();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el turno cabecera');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTurnosTotal]);

  /**
   * Elimina un turno total
   */
  const deleteTurnoTotal = useCallback(async (idTurnoCabecera) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.deleteTurnoTotal(idTurnoCabecera);
      showSuccessToast('Turno total eliminado exitosamente');
      
      // No llamar fetchTurnosTotal aquí, dejar que el componente maneje el refresh
      
      return response;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el turno total');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Estado
    turnos,
    turnosTotal,
    loading,
    error,
    
    // Funciones
    fetchTurnos,
    fetchTurnosTotal,
    fetchTurnosByProyecto,
    createTurnoDetalle,
    updateTurno,
    createTurnoCabecera,
    updateTurnoCabecera,
    deleteTurnoTotal,
    
    // Función para cargar IDs de comensales
    getComensalesIds: turnoApi.getComensalesIds,
    
    // Función para obtener documentos registrados en turno
    getDocumentosRegistradosByTurnoApi: turnoApi.getDocumentosRegistradosByTurnoApi,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useTurnos;
